<template>
  <Card dark tile flat>
    <Toolbar class="toolbar--height">
      <div style="display: flex">
        <div class="mr-4 text-uppercase pb-2">{{ this.activeFilter.deviceType['text'] }}</div>
      </div>
      <v-spacer></v-spacer>
      <v-flex class="py-2 mb-3 text-md-right">
        <Toggle :modelValue="toggle_button" @toggleValueChange="(val) => toggle_button = val"
          :theme="theme">
          <v-btn flat value="down_time">
            {{ $t('DOWN TIME') }}
          </v-btn>
          <v-btn flat value="down_energy">
            {{ $t('DOWN ENERGY') }}
          </v-btn>
          <v-btn flat value="compare_graph">
            {{ $t('GRAPH') }}
          </v-btn>
        </Toggle>
      </v-flex>
    </Toolbar>
    <div v-show="toggle_button === 'down_energy' || toggle_button === 'down_time'"
      class="map-legend">
      <MapLegend v-for="map in mapsLabel" :key="map.name" :text="map.name" :shape="map.attr" />
    </div>
    <div v-show="toggle_button === 'compare_graph'" class="download-icon">
      <v-btn flat icon color="grey" @click="downTimeSummaryDownload">
        <v-icon>get_app</v-icon>
      </v-btn>
    </div>
    <div class="graph" v-if="toggle_button === 'down_energy' || toggle_button === 'down_time'">
      <SunburstChart :options="graphOptions" :loading="loading"></SunburstChart>
    </div>
    <div v-else-if="toggle_button === 'compare_graph'">
      <GlobalGraph class="graph" v-if="series.length" :series="computedSeries" :xAxis="xAxis" :yAxis="yAxis" :grid="grid"
        :legendPosition="legend" :title="graphTitle" :isDownload="false" :toolBoxPosition="{top: 12,
          right: 45}" :theme="theme" :loading="loading" />
    </div>
  </Card>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
const momentTz = require('moment-timezone')
import {
  mapGetters,
  mapMutations
} from 'vuex'
import { unitConvertorMixin, mapGettersMixin } from '@/util/mixin/index'
import DownTimeAnalysis from '@/services/downtimeAnalysis'
import SunburstChart from '../downTimeAnalysis/analysisChart/SunburstChart'
import MapLegend from '@/components/common/MapLegend'

export default {
  components: {
    SunburstChart,
    MapLegend
  },
  props: {
    selectedId: {
      type: String,
      default: ''
    },
    plantId: {
      type: Number
    },
    callSummaryApi: {
      type: Number,
      default: 0
    },
    activeFilter: {
      type: Object,
      default: function() {
        return {}
      }
    }
  },
  mixins: [ unitConvertorMixin, mapGettersMixin ],
  data() {
    return {
      toggle_button: 'down_time',
      overviewData: {},
      downTimeOptions: [],
      energyLossOptions: [],
      invEnergyTimeList: [],
      isWatchEnabled: false,
      graphTitle: {
        text: 'Down Time vs Energy Loss',
        left: 'left',
        textStyle: {
          color: '#EEF1FA'
        }
      },
      grid: [{
        left: 60,
        right: 50,
        height: '35%'
      },
      {
        left: 60,
        right: 50,
        top: '55%',
        height: '35%'
      }
      ],
      legend: {
        left: 'center',
        right: 0,
        top: 12
      },
      xAxis: [{
        type: 'category',
        axisLine: {
          onZero: true,
          lineStyle: {
            color: '#B9B8CE'
          }
        },
        data: []
      },
      {
        gridIndex: 1,
        type: 'category',
        axisLine: {
          onZero: true,
          lineStyle: {
            color: '#B9B8CE'
          }
        },
        data: [],
        position: 'bottom'
      }
      ],
      yAxis: [{
        name: 'UP / DOWN',
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#B9B8CE'
          }
        }
      },
      {
        gridIndex: 1,
        name: 'Energy(kWh)',
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#B9B8CE'
          }
        }
      }
      ],
      series: [],
      loading: false,
      dateFrom: null,
      dateTo: null,
      type: '',
      tableHeader: [
        { key: 'Date', value: 'Date' },
        { key: 'Down Time (min)', value: 'Down Time' },
        { key: 'Energy Loss (kWh)', value: 'Energy Loss' }
      ],
      tableData: []
    }
  },
  computed: {
    ...mapGetters(['getGeneralState', 'getPlantName', 'getActivePlantTimezone']),
    currentLanguage() {
      return this.getGeneralState('language')
    },
    mapsLabel() {
      let maps = []
      if (this.toggle_button === 'down_time') {
        maps = [{
          name: this.$t('Not Down'),
          attr: {
            name: 'square',
            color: '#1fcf8e'
          }
        },
        {
          name: `${this.$t('Down Time')} <= 10 mins`,
          attr: {
            name: 'square',
            color: '#e6db72'
          }
        },
        {
          name: `${this.$t('Down Time')} > 10 mins`,
          attr: {
            name: 'square',
            color: '#f66747'
          }
        }
        ]
      } else {
        maps = [{
          name: this.$t('Not Down'),
          attr: {
            name: 'square',
            color: '#1fcf8e'
          }
        },
        {
          name: `${this.$t('Loss')} > 0 kWh`,
          attr: {
            name: 'square',
            color: '#f66747'
          }
        }
        ]
      }
      return maps
    },
    computedSeries() {
      const cloneSeries = _.cloneDeep(this.series)
      return cloneSeries.map(el => {
        if (el.name.includes('State')) {
          if (moment(this.dateFrom).isSame(this.dateTo)) {
            el.name = `${this.activeFilter.deviceType.text} ${this.$t('State')}`
          } else {
            el.name = this.$t('Down Time')
          }
        } else {
          el.name = this.$t(el.name)
        }
        return el
      })
    },
    activeWatch() {
      return `${this.dateFrom} ${this.dateTo} ${this.activeFilter.deviceType['value']}`
    },
    graphOptions() {
      return this.toggle_button === 'down_energy' ? this.energyLossOptions : this.downTimeOptions
    },
    watchDates() {
      return this.activeFilter.Date ? `${this.activeFilter.Date.from} ${this.activeFilter.Date.to}` : null
    },
    callSummary() {
      return `${this.selectedId} ${this.callSummaryApi}`
    }
  },
  mounted() {
    if (this.activeFilter.selectedRange.text !== 'Custom') {
      const param = this.computeParam()
      this.dateFrom = param.from
      this.dateTo = param.to
      this.type = param.type
    } else {
      this.dateFrom = this.activeFilter.Date.from
      this.dateTo = this.activeFilter.Date.to
      this.type = ''
    }
    this.$emit('selectedTab', this.toggle_button)
    this.downOverviewHandler()
  },
  methods: {
    ...mapMutations([ 'SET_PROGRESS_BAR' ]),
    computeParam() {
      // Function runs only when the selected range value is not custom
      const param = {}
      const date = momentTz().tz(this.getActivePlantTimezone)
      param['type'] = this.activeFilter['selectedRange'].interval
      if (this.activeFilter['selectedRange'].interval === 'daily') {
        if (this.activeFilter['selectedRange'].offsetLevel === 'month') {
          param.from = date.clone()
            .subtract(
              this.activeFilter['selectedRange'].startOffset,
              this.activeFilter['selectedRange'].offsetLevel
            )
            .startOf(this.activeFilter['selectedRange'].offsetLevel)
            .format('YYYY-MM-DD')
          param.to = date.clone()
            .subtract(
              this.activeFilter['selectedRange'].endOffset,
              this.activeFilter['selectedRange'].offsetLevel
            )
            .endOf(this.activeFilter['selectedRange'].offsetLevel)
            .format('YYYY-MM-DD')
        } else {
          param.from = date.clone()
            .subtract(this.activeFilter['selectedRange'].startOffset, 'day')
            .format('YYYY-MM-DD')
          param.to = date.clone()
            .subtract(this.activeFilter['selectedRange'].endOffset, 'day')
            .format('YYYY-MM-DD')
        }
      } else if (this.activeFilter['selectedRange'].interval === 'monthly') {
        let format = 'YYYY-MM-DD'
        param.from = date.clone()
          .subtract(
            this.activeFilter['selectedRange'].startOffset,
            this.activeFilter['selectedRange'].offsetLevel
          )
          .startOf(this.activeFilter['selectedRange'].offsetLevel)
          .format(`${format}`)
        param.to = date.clone()
          .subtract(
            this.activeFilter['selectedRange'].endOffset,
            this.activeFilter['selectedRange'].offsetLevel
          )
          .endOf(this.activeFilter['selectedRange'].offsetLevel)
          .format(`${format}`)
      } 
      return param
    },
    downOverviewHandler() {
      this.isWatchEnabled = true
      const activeDevice = this.activeFilter.deviceType['value']
      const param = {
        plantId: this.plantId,
        fromDate: this.dateFrom,
        toDate: this.dateTo,
        type: this.type,
        parameter: activeDevice
      }
      this.SET_PROGRESS_BAR(true)
      this.loading = true
      DownTimeAnalysis.getDowntimeOverview(param)
        .then((response) => {
          this.loading = false
          this.SET_PROGRESS_BAR(false)
          const dateTime = []
          const dateEnergy = []
          const sideList = []
          let totalTimeLoss = null
          let totalEnergyLoss = null
          const energyUnit = response.data.message.overview.unit.energy
          const timeUnit = response.data.message.overview.unit.time
          const blockList = Object.keys(response.data.message.overview.data)
          blockList.forEach(blk => {
            const blockEnergy = {
              name: blk,
              itemStyle: {},
              children: []
            }
            const blockTime = {
              name: blk,
              itemStyle: {},
              children: []
            }
            const roomList = Object.keys(response.data.message.overview.data[blk])
            let isRoom = false
            let isBlock = false
            let isBlockYellow = false
            let isRoomYellow = false
            let isRoomEnergy = false
            let isBlockEnergy = false
            roomList.forEach(rm => {
              const roomEnergy = {
                name: rm,
                itemStyle: {},
                children: []
              }
              const roomTime = {
                name: rm,
                itemStyle: {},
                children: []
              }
              blockTime.children.push(roomTime)
              blockEnergy.children.push(roomEnergy)
              const invList = Object.keys(response.data.message.overview.data[blk][rm])
              isRoom = invList.some(el => response.data.message.overview.data[blk][rm][el]['Down Time'] !== 0 &&
                response.data.message.overview.data[blk][rm][el]['Down Time'] > 10)
              isRoomYellow = invList.some(el => response.data.message.overview.data[blk][rm][el]['Down Time'] !== 0 &&
                response.data.message.overview.data[blk][rm][el]['Down Time'] <= 10)
              isBlock = isBlock || invList.some(el => response.data.message.overview.data[blk][rm][el]['Down Time'] !== 0 &&
                response.data.message.overview.data[blk][rm][el]['Down Time'] > 10)
              isBlockYellow = isBlockYellow || invList.some(el => response.data.message.overview.data[blk][rm][el]['Down Time'] !== 0 &&
                response.data.message.overview.data[blk][rm][el]['Down Time'] <= 10)
              isRoomEnergy = invList.some(el => response.data.message.overview.data[blk][rm][el]['Energy Loss'] !== 0)
              isBlockEnergy = isBlockEnergy || invList.some(el => response.data.message.overview.data[blk][rm][el]['Energy Loss'] !== 0)
              if (isRoom) {
                roomTime.itemStyle['color'] = '#f66747'
              } else if (isRoomYellow) {
                roomTime.itemStyle['color'] = '#e6db72'
              } else {
                roomTime.itemStyle['color'] = '#1fcf8e'
              }

              if (isRoomEnergy) {
                roomEnergy.itemStyle['color'] = '#f66747'
              } else {
                roomEnergy.itemStyle['color'] = '#1fcf8e'
              }

              invList.forEach(inv => {
                const inverterEnergy = {
                  name: '',
                  value: 1,
                  itemStyle: {}
                }
                const inverterTime = {
                  name: '',
                  value: 1,
                  itemStyle: {}
                }
                roomTime.children.push(inverterTime)
                roomEnergy.children.push(inverterEnergy)

                const id = response.data.message.overview.data[blk][rm][inv]['id']
                let time = response.data.message.overview.data[blk][rm][inv]['Down Time']
                let energy = response.data.message.overview.data[blk][rm][inv]['Energy Loss']
                totalTimeLoss += time
                totalEnergyLoss += energy
                const listObj = {
                  name: `${blk}-${rm}-${inv}`,
                  id,
                  'energy loss': null,
                  'down time': null
                }
                listObj['energy loss'] = `${this.convert(energy, energyUnit, 'MWh')}`
                listObj['down time'] = `${this.minuteTOHours(time, timeUnit, 'hr')}`
                sideList.push(listObj)
                if (time !== 0 && time > 10) {
                  inverterTime.name = `${inv} \n ${this.minuteTOHours(time, timeUnit, 'hr')}`
                  inverterTime.itemStyle['color'] = '#f7785c'
                } else if (time !== 0 && time <= 10) {
                  inverterTime.name = `${inv} \n ${this.minuteTOHours(time, timeUnit, 'hr')}`
                  inverterTime.itemStyle['color'] = '#e9df81'
                } else {
                  inverterTime.name = inv
                  inverterTime.itemStyle['color'] = '#22E69E'
                }

                if (energy !== 0) {
                  inverterEnergy.name = `${inv} \n ${this.convert(energy, energyUnit, 'MWh')}`
                  inverterEnergy.itemStyle['color'] = '#f7785c'
                } else {
                  inverterEnergy.name = inv
                  inverterEnergy.itemStyle['color'] = '#22E69E'
                }
              })
            })
            if (isBlock) {
              blockTime.itemStyle['color'] = '#f55633'
            } else if (isBlockYellow) {
              blockTime.itemStyle['color'] = '#e3d762'
            } else {
              blockTime.itemStyle['color'] = '#1bb87e'
            }

            if (isBlockEnergy) {
              blockEnergy.itemStyle['color'] = '#f55633'
            } else {
              blockEnergy.itemStyle['color'] = '#1bb87e'
            }
            dateTime.push(blockTime)
            dateEnergy.push(blockEnergy)
          })
          this.downTimeOptions = [...dateTime]
          this.energyLossOptions = [...dateEnergy]
          this.invEnergyTimeList = [...sideList]
          const totalEnergyLossWithUnit = this.convert(totalEnergyLoss, energyUnit, 'MWh')
          const totalTimeLossWithUnit = this.minuteTOHours(totalTimeLoss, timeUnit, 'hr')
          this.$emit('totalCardData', {
            'energy loss': `${totalEnergyLossWithUnit}`,
            'time loss': `${totalTimeLossWithUnit}`
          })
          this.$emit('sideCardData', this.invEnergyTimeList)
        })
        .catch(() => {
          this.loading = false
          this.SET_PROGRESS_BAR(false)
          this.downTimeOptions = []
          this.energyLossOptions = []
          this.invEnergyTimeList = []
          this.$emit('totalCardData', {
            'energy loss': 'No Data',
            'time loss': 'No Data'
          })
          this.$emit('sideCardData', this.invEnergyTimeList)
        })
    },
    downEnergyVsTimeHandler() {
      this.isWatchEnabled = true
      this.loading = true
      let graphObj1 = {
        name: '',
        type: 'line',
        symbolSize: 8,
        data: []
      }
      if (moment(this.dateFrom).isSame(this.dateTo)) {
        graphObj1.name = `${this.activeFilter.deviceType.text} State`
      } else {
        graphObj1.name = 'Down Time'
      }

      let graphObj2 = {
        name: 'Energy Loss',
        type: 'line',
        xAxisIndex: 1,
        yAxisIndex: 1,
        symbolSize: 8,
        data: []
      }
      const param = {
        plantId: this.plantId,
        fromDate: this.dateFrom,
        toDate: this.dateTo,
        type: this.type,
        parameter: this.activeFilter.deviceType.value,
        id: this.selectedId
      }

      DownTimeAnalysis.getDowntimeSummary(param)
        .then((response) => {
          this.loading = false
          const data = response.data.message
          const xAxisData = []
          const sortedData = data.sort((a, b) => moment(a.sensor_time).diff(b.sensor_time))
          this.tableData = []
          sortedData.forEach(item => {
            const tableElement = {}
            tableElement['Energy Loss (kWh)'] = (item.energy).toFixed(2)
            graphObj1.data.push(item.value)
            graphObj2.data.push((item.energy).toFixed(2))
            let timeOrDate = null
            if (moment(this.dateFrom).isSame(this.dateTo)) {
              timeOrDate = moment.unix(item.sensor_time).format('h:mm A')
              this.$set(this.yAxis[0], 'max', 1)
              this.$set(this.yAxis[0], 'interval', 1)
              tableElement['State'] = item.value
            } else {
              timeOrDate = moment.unix(item.sensor_time).format('YYYY-MM-DD')
              xAxisData.push()
              if (this.yAxis[0].max) {
                delete this.yAxis[0].max
              }

              if (this.yAxis[0].interval) {
                delete this.yAxis[0].interval
              }
              graphObj1.type = 'bar'
              graphObj2.type = 'bar'
              tableElement['Down Time (min)'] = item.value
            }
            xAxisData.push(timeOrDate)
            tableElement['Date'] = timeOrDate
            this.tableData.push(tableElement)
          })
          this.xAxis.forEach((xA, index) => {
            this.xAxis[index].data = xAxisData
          })
          this.series = _.cloneDeep([graphObj1, graphObj2])
          if (moment(this.dateFrom).isSame(this.dateTo)) {
            this.tableHeader = [
              { key: this.$t('Date'), value: 'Date' },
              { key: this.$t('State'), value: 'State' },
              { key: this.$t('Energy Loss (kWh)'), value: 'Energy Loss' }
            ]
          } else {
            this.tableHeader = [
              { key: this.$t('Date'), value: 'Date' },
              { key: this.$t('Down Time (min)'), value: 'Down Time' },
              { key: this.$t('Energy Loss (kWh)'), value: 'Energy Loss' }
            ]
          }
        })
        .catch(() => {
          this.loading = false
          this.series = []
          this.tableData = []
          this.xAxis.forEach((xA, index) => {
            this.xAxis[index].data = []
          })
        })
    },
    async downTimeSummaryDownload() {
      try {
        const param = {
          plantId: this.plantId,
          plantName: this.getPlantName(this.plantId),
          deviceType: this.activeFilter.deviceType['text'],
          data: this.tableData,
          header: this.tableHeader
        }

        this.SET_PROGRESS_BAR(true)
        const response = await DownTimeAnalysis.downloadDowntimeSummary(param)
        this.SET_PROGRESS_BAR(false)
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `downTimeAnalysis-${momentTz().tz(this.getActivePlantTimezone).format('YYYY_MM_DD_HH_mm_ss')}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.SET_PROGRESS_BAR(false)
      }
    }
  },
  watch: {
    currentLanguage() {
      this.$set(this.yAxis[0], 'name', `${this.$t('UP')} / ${this.$t('DOWN')}`)
      this.$set(this.yAxis[1], 'name', `${this.$t('Energy')} (kWh)`)
      this.$set(this.graphTitle, 'text', `${this.$t('Down Time')} vs ${this.$t('Energy Loss')}`)
      this.tableHeader = [
        { key: this.$t('Date'), value: 'Date' },
        { key: `${this.$t('Down Time')} (min)`, value: 'Down Time' },
        { key: `${this.$t('Energy Loss')} (kWh)`, value: 'Energy Loss' }
      ]
    },
    toggle_button(value) {
      this.$emit('selectedTab', value)
    },
    'activeFilter.selectedRange.text'() {
      if (this.activeFilter.selectedRange.text !== 'Custom') {
        const param = this.computeParam()
        this.dateFrom = param.from
        this.dateTo = param.to
        this.type = param.type
      } else {
        this.dateFrom = this.activeFilter.Date.from
        this.dateTo = this.activeFilter.Date.to
        this.type = ''
      }
    },
    watchDates(val, oldVal) {
      if (this.activeFilter.selectedRange.text === 'Custom' && oldVal !== null) {
        this.dateFrom = val.split(' ')[0]
        this.dateTo = val.split(' ')[1]
      }
    },
    activeWatch(val, oldVal) {
      if (this.isWatchEnabled && !oldVal.includes('null')) {
        this.downOverviewHandler()
        if (this.toggle_button === 'compare_graph') {
          this.downEnergyVsTimeHandler()
        }
      }
    },
    callSummary() {
      this.downEnergyVsTimeHandler()
    }
  }
}
</script>

<style scoped>
.graph {
  height: 65vh;
  width: 100%;
}

.toolbar--height {
  height: 50px;
}

.map-legend {
  position: absolute;
  right: 0.875rem;
  top: 22vh;
}

.download-icon {
  position: absolute;
  right: 0.625rem;
  top: 3.75rem;
  z-index: 2;
}
</style>
